import { ConnectProto } from "@racoon/api";
import { Namespace, useT } from "@racoon/i18n";
import { useToast } from "@racoon/ui";

import {
  useCancelJobMutation,
  useLazyListJobsQuery,
  useUpdateJobMutation,
} from "~/shared/api/jobs/job.grpc";
import { cancelledJobUpdate, jobOrgIds } from "~/shared/lib/job-status";
import { findRetryJobs, orderAttemptsQuery, WAITING_STATUSES } from "./outcome-correction";

export type RetryCancel = { ok: false } | { ok: true; cancelled?: ConnectProto.Job };

/**
 * Cancels the waiting retry job before a correction out of Невывоз: the backend refuses Выполнено
 * while it is open. `ok: false` holds the correction back.
 */
export const useCancelRetryJob = () => {
  const t = useT();
  const { toast } = useToast();
  const [listJobs] = useLazyListJobsQuery();
  const [cancelJob] = useCancelJobMutation();
  const [updateJob] = useUpdateJobMutation();

  return async (job: ConnectProto.Job): Promise<RetryCancel> => {
    if (!job.orderNumber) return { ok: true };
    // The status is held back: the dispatcher cancels the retry job by hand
    const holdBack = (): RetryCancel => {
      toast({
        variant: "destructive",
        title: t(Namespace.PhotoReport, "retryCancelFailed", { jobNumber: job.orderNumber }),
      });
      return { ok: false };
    };
    try {
      const { jobs } = await listJobs({
        ...orderAttemptsQuery(job),
        pickupLocationIds: [job.pickupLocationId],
        jobStatuses: WAITING_STATUSES,
      }).unwrap();
      const retries = findRetryJobs(job, jobs);
      // Two waiting attempts: cancel neither rather than guess
      if (retries.length > 1) return holdBack();
      const [retry] = retries;
      if (!retry) return { ok: true };
      // On a route, CancelJob also closes the route if this was its last open job
      await (
        retry.routeId
          ? cancelJob({ id: retry.id, ...jobOrgIds(retry) })
          : updateJob(cancelledJobUpdate(retry))
      ).unwrap();
      return { ok: true, cancelled: retry };
    } catch (error) {
      console.error("cancel retry job", job.orderNumber, error);
      return holdBack();
    }
  };
};
